import { useState } from 'react';
import {
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Typography,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAppSelector } from 'store/hook';
import TireInstallationHandler from 'handlers/tire-installation.handler';
import {
  type UninstallTireRequest,
  type ReasonForRemoval,
  type TireStatus,
} from 'types/tire-inventory.types';
import { ThemePalette } from 'mui.theme';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  tireSerialNumber: string;
  installationId: string;
  assetName: string;
  position: string;
  open: boolean;
  handleAbort: () => void;
  enableDarkTheme: boolean;
  onSuccess?: () => Promise<void>;
}

export const UninstallTireModal = ({
  tireSerialNumber,
  installationId,
  assetName,
  position,
  open,
  handleAbort,
  enableDarkTheme,
  onSuccess,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const customerId = useAppSelector(
    (state) => state.persistedReducer.customer.id
  );

  const tireInstallationHandler = new TireInstallationHandler();

  const [formData, setFormData] = useState<UninstallTireRequest>({
    installationId,
    reasonForRemoval: 'EXCESSIVE_WEAR',
    odometerAtRemoval: 0,
    engineHoursAtRemoval: 0,
    newTireStatus: 'IN_STORAGE',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);

  const reasonForRemovalOptions: Array<{
    value: ReasonForRemoval;
    label: string;
  }> = [
    { value: 'PM_ROTATION', label: t('pm_rotation') },
    { value: 'SCRAP', label: t('scrap') },
    { value: 'FLAT', label: t('flat_tire') },
    { value: 'EXCESSIVE_WEAR', label: t('excessive_wear') },
    { value: 'OTHER', label: t('other') },
  ];

  const tireStatusOptions: Array<{ value: TireStatus; label: string }> = [
    { value: 'IN_STORAGE', label: t('in_storage') },
    { value: 'AT_REPAIR', label: t('at_repair') },
    { value: 'SCRAPPED', label: t('scrapped') },
  ];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.reasonForRemoval) {
      newErrors.reasonForRemoval = 'Reason for removal is required';
    }
    if (
      formData.odometerAtRemoval === undefined ||
      formData.odometerAtRemoval === null
    ) {
      newErrors.odometerAtRemoval = 'Odometer reading is required';
    } else if (formData.odometerAtRemoval < 0) {
      newErrors.odometerAtRemoval = 'Must be a positive number';
    }
    if (
      formData.engineHoursAtRemoval === undefined ||
      formData.engineHoursAtRemoval === null
    ) {
      newErrors.engineHoursAtRemoval = 'Engine hours is required';
    } else if (formData.engineHoursAtRemoval < 0) {
      newErrors.engineHoursAtRemoval = 'Must be a positive number';
    }
    if (!formData.newTireStatus) {
      newErrors.newTireStatus = 'New tire status is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof UninstallTireRequest, value: any) => {
    setFormData({
      ...formData,
      [field]: value === '' ? undefined : value,
    });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const success = await tireInstallationHandler.uninstallTire(
        formData,
        customerId
      );

      if (success) {
        // Wait for the inventory to refresh before closing the modal
        await onSuccess?.();
        handleAbort();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid =
    !!formData.reasonForRemoval &&
    formData.odometerAtRemoval !== undefined &&
    formData.engineHoursAtRemoval !== undefined &&
    !!formData.newTireStatus;

  return (
    <Dialog
      open={open}
      onClose={handleAbort}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: enableDarkTheme
            ? palette.modalBackground
            : palette.boxBackground,
          backgroundImage: 'none',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: 2.5,
          px: 3,
          color: palette.textPrimary,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            component="div"
            sx={{ fontWeight: 600, mb: 0.5 }}
          >
            {t('uninstall_tire_from_asset')}
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.7, fontSize: '0.875rem' }}
          >
            {t('tire_serial_number_2')} <strong>{tireSerialNumber}</strong>
          </Typography>
          <Typography
            variant="body2"
            sx={{ opacity: 0.7, fontSize: '0.875rem' }}
          >
            {t('currently_installed_on')}{' '}
            <strong>
              {assetName} - {position}
            </strong>
          </Typography>
        </Box>
        <IconButton
          onClick={handleAbort}
          sx={{
            color: palette.textPrimary,
            '&:hover': {
              backgroundColor: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3, px: 3 }}>
        <Grid container spacing={2} mt={1}>
          {/* Reason for Removal */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!errors.reasonForRemoval}>
              <InputLabel
                sx={{
                  color: enableDarkTheme
                    ? ThemePalette.typography.white
                    : 'rgba(0, 0, 0, 0.6)',
                }}
              >
                {t('reason_for_removal')}
              </InputLabel>
              <Select
                value={formData.reasonForRemoval ?? ''}
                label={t('reason_for_removal')}
                disabled={isLoading}
                onChange={(e) => {
                  handleChange('reasonForRemoval', e.target.value);
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1.5px',
                  },
                  color: palette.textPrimary,
                }}
              >
                {reasonForRemovalOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.reasonForRemoval && (
                <FormHelperText>{errors.reasonForRemoval}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* New Tire Status */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!errors.newTireStatus}>
              <InputLabel
                sx={{
                  color: enableDarkTheme
                    ? ThemePalette.typography.white
                    : 'rgba(0, 0, 0, 0.6)',
                }}
              >
                {t('new_tire_status')}
              </InputLabel>
              <Select
                value={formData.newTireStatus ?? ''}
                label={t('new_tire_status')}
                disabled={isLoading}
                onChange={(e) => {
                  handleChange('newTireStatus', e.target.value);
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1.5px',
                  },
                  color: palette.textPrimary,
                }}
              >
                {tireStatusOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.newTireStatus && (
                <FormHelperText>{errors.newTireStatus}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          {/* Odometer at Removal */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              label={t('odometer_at_removal')}
              placeholder={t('e_g_55000')}
              disabled={isLoading}
              value={formData.odometerAtRemoval ?? ''}
              onChange={(e) => {
                handleChange(
                  'odometerAtRemoval',
                  e.target.value ? parseFloat(e.target.value) : undefined
                );
              }}
              error={!!errors.odometerAtRemoval}
              helperText={
                errors.odometerAtRemoval ?? 'Odometer reading at removal'
              }
              inputProps={{ min: 0, step: 1 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '1.5px',
                  },
                },
              }}
            />
          </Grid>

          {/* Engine Hours at Removal */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              type="number"
              label={t('engine_hours_at_removal')}
              placeholder={t('e_g_6868')}
              disabled={isLoading}
              value={formData.engineHoursAtRemoval ?? ''}
              onChange={(e) => {
                handleChange(
                  'engineHoursAtRemoval',
                  e.target.value ? parseFloat(e.target.value) : undefined
                );
              }}
              error={!!errors.engineHoursAtRemoval}
              helperText={
                errors.engineHoursAtRemoval ?? 'Engine hours at removal'
              }
              inputProps={{ min: 0, step: 0.1 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderWidth: '1.5px',
                  },
                },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1.5 }}>
        <Button
          onClick={handleAbort}
          variant="outlined"
          size="large"
          disabled={isLoading}
          sx={{
            color: enableDarkTheme
              ? ThemePalette.typography.white
              : ThemePalette.typography.primary,
            borderColor: enableDarkTheme
              ? ThemePalette.typography.white
              : ThemePalette.typography.primary,
            borderWidth: '1.5px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            '&:hover': {
              borderWidth: '1.5px',
              backgroundColor: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)',
            },
            '&:disabled': {
              color: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.3)'
                : 'rgba(0, 0, 0, 0.26)',
              borderColor: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.3)'
                : 'rgba(0, 0, 0, 0.26)',
            },
          }}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="large"
          disabled={!isFormValid || isLoading}
          sx={{
            backgroundColor: '#d32f2f', // Red color for uninstall action
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            boxShadow: 2,
            '&:hover': {
              backgroundColor: '#b71c1c',
              boxShadow: 3,
            },
            '&:disabled': {
              backgroundColor: enableDarkTheme
                ? 'rgba(255, 255, 255, 0.12)'
                : 'rgba(0, 0, 0, 0.12)',
            },
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} sx={{ color: 'white' }} />
              Uninstalling...
            </Box>
          ) : (
            'Uninstall Tire'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
